import React, { useState } from 'react';
import { supabase } from './supabase';
import { t } from './translations';

// ─── TIME-LOCKED JARS ────────────────────────────────────────────────────────
// Jar owner sets an unlock date. Until then the jar shows a countdown
// and all glows are hidden. On unlock date, they're revealed all at once.
// Stored in profiles.unlock_at (timestamptz, nullable)

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - Date.now();
  return Math.ceil(diff / 86400000);
}

// ── Lock Settings Panel ───────────────────────────────────────────────────────
export function TimeLockSettings({ profile, lang, onSaved }) {
  const T = t[lang];
  const [unlockDate, setUnlockDate] = useState(
    profile?.unlock_at ? new Date(profile.unlock_at).toISOString().slice(0,10) : ''
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Minimum tomorrow
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0,10);

  async function save() {
    setSaving(true);
    await supabase.from('profiles')
      .update({ unlock_at: unlockDate ? new Date(unlockDate).toISOString() : null })
      .eq('id', profile.id);
    setSaving(false); setSaved(true);
    setTimeout(()=>setSaved(false), 2000);
    if (onSaved) onSaved(unlockDate || null);
  }

  async function unlock() {
    setUnlockDate('');
    setSaving(true);
    await supabase.from('profiles').update({ unlock_at: null }).eq('id', profile.id);
    setSaving(false);
    if (onSaved) onSaved(null);
  }

  const days = daysUntil(unlockDate);
  const PRESETS = [
    { label: lang==='ar'?'أسبوع':'1 week', days:7 },
    { label: lang==='ar'?'شهر':'1 month', days:30 },
    { label: lang==='ar'?'يوم ميلادي 🎂':'My birthday 🎂', days:null, special: true },
  ];

  return (
    <div style={{background:'rgba(45,212,191,0.05)',border:'1px solid rgba(45,212,191,0.18)',borderRadius:'var(--radius-sm)',padding:18,marginBottom:20}}>
      <div style={{fontWeight:600,fontSize:'0.88rem',marginBottom:4,color:'var(--teal)'}}>
        ⏰ {lang==='ar'?'جرّة مقفولة (اختياري)':'Time-lock your jar (optional)'}
      </div>
      <p style={{fontSize:'0.78rem',color:'var(--text-muted)',marginBottom:12,lineHeight:1.6}}>
        {lang==='ar'
          ?'خلي جرّتك مقفولة لحد تاريخ معين — الناس تبعت أضواء بس إنت مش تشوفهم إلا في الموعد'
          :"Lock your jar until a date — people can still send glows but you won't see them until it unlocks"}
      </p>

      <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
        {PRESETS.map((p,i)=>(
          <button key={i} onClick={()=>{
            if(p.special) return; // Open date picker for special
            const d = new Date(Date.now() + p.days*86400000).toISOString().slice(0,10);
            setUnlockDate(d);
          }}
            style={{fontSize:'0.75rem',padding:'5px 12px',borderRadius:100,background:'var(--bg3)',border:'1px solid var(--border)',color:'var(--text-muted)',cursor:'pointer',fontFamily:'inherit'}}>
            {p.label}
          </button>
        ))}
      </div>

      <div className="form-group" style={{marginBottom:12}}>
        <label className="form-label">{lang==='ar'?'تاريخ الفتح':'Unlock date'}</label>
        <input type="date" min={tomorrow} value={unlockDate} onChange={e=>setUnlockDate(e.target.value)}
          style={{textAlign:'left',direction:'ltr'}}/>
        {days !== null && days > 0 && (
          <span style={{fontSize:'0.72rem',color:'var(--teal)',marginTop:4,display:'block'}}>
            {lang==='ar'?`هتفتح بعد ${days} يوم ✨`:`Unlocks in ${days} days ✨`}
          </span>
        )}
      </div>

      <div style={{display:'flex',gap:8}}>
        <button className="btn" style={{background:'var(--teal)',color:'#001A18',fontWeight:700,fontSize:'0.82rem',padding:'9px 18px',border:'none',borderRadius:'var(--radius-sm)',cursor:'pointer'}} onClick={save} disabled={saving||!unlockDate}>
          {saving?'…':saved?(lang==='ar'?'✓ اتحفظ':'✓ Saved'):(lang==='ar'?'قفل الجرّة':'Lock jar')}
        </button>
        {profile?.unlock_at && (
          <button className="btn btn-ghost" style={{fontSize:'0.82rem',padding:'9px 18px'}} onClick={unlock}>
            {lang==='ar'?'فتح دلوقتي':'Unlock now'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Locked Jar Banner (shown in Inbox when jar is locked) ─────────────────────
export function LockedJarBanner({ unlockAt, messageCount, lang }) {
  if (!unlockAt) return null;
  const days = daysUntil(unlockAt);
  const isUnlocked = days !== null && days <= 0;
  if (isUnlocked) return null; // Jar is now open, show messages normally

  const unlockDateStr = new Date(unlockAt).toLocaleDateString(
    lang==='ar'?'ar-EG':'en-GB',
    {day:'numeric',month:'long',year:'numeric'}
  );

  return (
    <div style={{background:'linear-gradient(135deg,rgba(45,212,191,0.08),rgba(45,212,191,0.04))',border:'1px solid rgba(45,212,191,0.25)',borderRadius:20,padding:'24px',marginBottom:20,textAlign:'center'}}>
      <div style={{fontSize:'3rem',marginBottom:12,filter:'drop-shadow(0 0 14px rgba(45,212,191,0.4))'}}>🔒</div>
      <div style={{fontWeight:700,fontSize:'1.1rem',color:'var(--teal)',marginBottom:8}}>
        {lang==='ar'?'جرّتك مقفولة':'Your jar is sealed'}
      </div>
      <p style={{color:'var(--text-muted)',fontSize:'0.88rem',lineHeight:1.65,marginBottom:16}}>
        {lang==='ar'
          ?`${messageCount} ضو بيستناك داخل الجرّة — هتتفتح في ${unlockDateStr}`
          :`${messageCount} glow${messageCount!==1?'s':''} waiting inside — unseals on ${unlockDateStr}`}
      </p>
      <div style={{background:'rgba(45,212,191,0.12)',border:'1px solid rgba(45,212,191,0.2)',borderRadius:100,padding:'8px 20px',display:'inline-block'}}>
        <span style={{color:'var(--teal)',fontWeight:600,fontSize:'1.4rem'}}>{days}</span>
        <span style={{color:'var(--text-muted)',fontSize:'0.82rem',marginLeft:6}}>
          {lang==='ar'?`يوم متبقي`:`day${days!==1?'s':''} to go`}
        </span>
      </div>
    </div>
  );
}
